/**
 * migratePreDepartureToVoyageSettings.js
 *
 * FT-126: Migrate pre-departure checks from checklist_configs to voyage_settings
 *
 * This is a ONE-TIME migration that:
 * 1. Reads pre-departure items from checklist_configs.pre_departure.items
 * 2. Copies them into voyage_settings.operationsList[phaseType='pre_departure'].checks
 * 3. REPLACES any existing pre_departure checks in voyage_settings (test data)
 * 4. Sets migration flag to prevent re-running
 *
 * CRITICAL: This migration handles vessel-scoped storage keys
 */

const MIGRATION_FLAG = 'sr.migration.preDepToVoyageSettings_v1';

/**
 * Run migration for pre-departure checklist unification
 * @returns {boolean} - True if migration ran, false if skipped
 */
export const migratePreDepartureToVoyageSettings = () => {
  // Check if already migrated
  if (localStorage.getItem(MIGRATION_FLAG) === 'complete') {
    console.log('[FT-126] Pre-departure migration already complete');
    return false;
  }

  try {
    console.log('[FT-126] Starting pre-departure checklist migration...');

    // Get current vessel ID from localStorage (set by vessel guard)
    // Note: Vessel scoping is handled by the app, but we need to know current vessel
    // to construct the correct vessel-scoped key
    const currentVesselId = localStorage.getItem('sr.currentVesselId');

    if (!currentVesselId) {
      console.warn('[FT-126] No current vessel ID found, migration will use non-scoped keys');
    }

    // Read checklist_configs (may be vessel-scoped or not)
    // Try both patterns: vessel-scoped and non-scoped
    const checklistConfigsKey = currentVesselId
      ? `checklist_configs_${currentVesselId}`
      : 'checklist_configs';

    let checklistConfigsRaw = localStorage.getItem(checklistConfigsKey);

    // Fallback to non-scoped if vessel-scoped doesn't exist
    if (!checklistConfigsRaw && currentVesselId) {
      checklistConfigsRaw = localStorage.getItem('checklist_configs');
    }

    if (!checklistConfigsRaw) {
      console.log('[FT-126] No checklist_configs found, skipping migration');
      localStorage.setItem(MIGRATION_FLAG, 'complete');
      return false;
    }

    const checklistConfigs = JSON.parse(checklistConfigsRaw);
    const preDepConfig = checklistConfigs?.pre_departure;

    if (!preDepConfig?.items || preDepConfig.items.length === 0) {
      console.log('[FT-126] No pre_departure items in checklist_configs, skipping');
      localStorage.setItem(MIGRATION_FLAG, 'complete');
      return false;
    }

    console.log(`[FT-126] Found ${preDepConfig.items.length} pre-departure items to migrate`);

    // Read voyage_settings (same scoping logic)
    const voyageSettingsKey = currentVesselId
      ? `voyage_settings_${currentVesselId}`
      : 'voyage_settings';

    let voyageSettingsRaw = localStorage.getItem(voyageSettingsKey);

    // Fallback to non-scoped if vessel-scoped doesn't exist
    if (!voyageSettingsRaw && currentVesselId) {
      voyageSettingsRaw = localStorage.getItem('voyage_settings');
    }

    const voyageSettings = voyageSettingsRaw ? JSON.parse(voyageSettingsRaw) : {};

    // Ensure operationsList exists
    if (!voyageSettings.operationsList) {
      voyageSettings.operationsList = [];
    }

    // Find or create pre_departure entry
    let preDepOp = voyageSettings.operationsList.find(op => op.phaseType === 'pre_departure');

    if (!preDepOp) {
      // Create new entry (use defaults from operationsConfig.js DEFAULT_PHASE_OPERATIONS)
      preDepOp = {
        id: 'pre_departure',
        label: 'Pre-Departure Checks',
        permanent: true,
        phaseType: 'pre_departure',
        fields: [],
        checks: []
      };
      voyageSettings.operationsList.unshift(preDepOp);
    }

    // REPLACE checks with migrated data (this is the critical part)
    // Convert from checklist_configs format to voyage_settings format
    preDepOp.checks = preDepConfig.items.map(item => ({
      id: item.id,
      text: item.text,
      required: item.required !== false, // Default to true if not specified
      // Preserve any additional properties from original item
      ...(item.hasNotes && { hasNotes: item.hasNotes }),
      ...(item.type && { type: item.type })
    }));

    // Save updated voyage_settings (use the same key we read from)
    const saveKey = voyageSettingsRaw
      ? (currentVesselId && localStorage.getItem(`voyage_settings_${currentVesselId}`)
          ? `voyage_settings_${currentVesselId}`
          : 'voyage_settings')
      : 'voyage_settings';

    localStorage.setItem(saveKey, JSON.stringify(voyageSettings));

    // Mark migration complete
    localStorage.setItem(MIGRATION_FLAG, 'complete');

    console.log(`[FT-126] ✅ Migration complete: ${preDepOp.checks.length} pre-departure checks migrated to ${saveKey}`);
    console.log('[FT-126] Original data in checklist_configs preserved (not deleted)');

    return true;

  } catch (error) {
    console.error('[FT-126] ❌ Migration failed:', error);
    // Don't set flag on error - allow retry on next boot
    return false;
  }
};
